import React, { useEffect, useState, useRef } from 'react';
import { VanityAPI } from '../services/api';
import { HighlightArticle, Issue, ArticleCategory } from '../types';
import ContentModuleEditor from '../components/ContentModuleEditor';
import StandardImageUploader from '../components/StandardImageUploader';
import SEOPanel from '../components/SEOPanel';
import RichTextEditor from '../components/RichTextEditor';

interface AdminHighlightsProps {
    onEdit?: (highlight: any) => void;
}

const AdminHighlights: React.FC<AdminHighlightsProps> = () => {
    // VIEW STATE
    const [view, setView] = useState<'list' | 'editor'>('list');

    // DATA STATE
    const [highlights, setHighlights] = useState<HighlightArticle[]>([]);
    const [issues, setIssues] = useState<Issue[]>([]);
    const [categories, setCategories] = useState<any[]>([]); // Added Categories
    const [loading, setLoading] = useState(true);

    // EDITOR STATE
    const [editingItem, setEditingItem] = useState<Partial<HighlightArticle> | null>(null);
    const [editTab, setEditTab] = useState<'content' | 'seo'>('content');
    const [tagInput, setTagInput] = useState('');

    // UPLOAD STATE
    const [showUploader, setShowUploader] = useState(false);
    const [stashedFile, setStashedFile] = useState<File | null>(null);
    const [uploadTarget, setUploadTarget] = useState<'cover' | 'module'>('cover');
    const [moduleTargetId, setModuleTargetId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // TOAST STATE
    const [toast, setToast] = useState<string | null>(null);
    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(null), 3000);
    };

    const init = async () => {
        setLoading(true);
        try {
            const [h, i, c] = await Promise.all([
                VanityAPI.getAdminHighlights(),
                VanityAPI.getAllMagazines(),
                VanityAPI.getCategories() // Fetch Categories
            ]);
            setHighlights(h);
            setIssues(i as Issue[]);
            setCategories(c || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    // Alt Text Auto-Save
    useEffect(() => {
        const handler = setTimeout(() => {
            if (editingItem?.heroImageId && editingItem?.heroAltText !== undefined && editingItem.heroImageId !== 'new') {
                VanityAPI.updateImageAltText(editingItem.heroImageId, editingItem.heroAltText)
                    .then(() => console.log("Hero Alt Saved"))
                    .catch(e => console.error(e));
            }
        }, 1500);
        return () => clearTimeout(handler);
    }, [editingItem?.heroAltText, editingItem?.heroImageId]);

    const handleCreate = () => {
        setEditingItem({
            id: 'new',
            title: '',
            status: 'draft',
            contentModules: [],
            tags: [],
            issueId: '', // Unassigned
        });
        setView('editor');
    };

    const handleEdit = async (id: string) => {
        try {
            const item = await VanityAPI.getAdminHighlight(id);
            console.log("FETCHED DATA (Rule 37 Verification):", item);
            console.log("DEBUG: Highlight loaded for edit:", { id, contentLen: item.content?.length });
            setEditingItem(item);
            setView('editor');
        } catch (err) {
            showToast('Failed to load item');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this highlight?')) return;
        await VanityAPI.deleteHighlight(id);
        init();
    };

    const handleSave = async () => {
        if (!editingItem) return;

        try {
            // Rule 18 & 72: Mandatory Integrity Check
            if (!editingItem.title) return showToast('Title is required');

            const safeSlug = editingItem.slug || editingItem.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');

            // Rule 74/75: Autonomous Payload Construction (Eliminating metaKeywords)
            const selectedCat = categories.find(c => c.id === editingItem.categoryId);

            const payload = {
                id: editingItem.id,
                title: editingItem.title,
                slug: safeSlug,
                content: editingItem.content || '',
                text: editingItem.text || '',
                status: editingItem.status || 'draft',
                issueId: editingItem.issueId || null,
                categoryId: editingItem.categoryId || null,
                category: selectedCat ? selectedCat.name : editingItem.category,
                tags: editingItem.tags || [],
                heroImageId: editingItem.heroImageId || null,
                contentModules: editingItem.contentModules || [],
                // THE BIG SIX - Explicit Mapping (Rule 60)
                seoTitle: editingItem.seoTitle || '',
                seoDescription: editingItem.seoDescription || '',
                seoKeywords: editingItem.seoKeywords || '',
                ogTitle: editingItem.ogTitle || '',
                ogDescription: editingItem.ogDescription || '',
                canonicalUrl: editingItem.canonicalUrl || `https://www.vanity.co.at/highlights/${safeSlug}`,
                seoScore: editingItem.seoScore || 0,
                layoutConfig: editingItem.layoutConfig || {}
            };

            console.log("ANTIGRAVITY ATTEMPT - Payload:", payload);
            await VanityAPI.saveHighlight(payload);

            showToast('Highlight Erfolgreich Gespeichert');
            setTimeout(() => setView('list'), 1000);
            init();
        } catch (err: any) {
            showToast('Save Error: ' + (err.message || 'Check Console'));
            console.error("CRITICAL SAVE FAILURE:", err);
        }
    };


    const handleUpdateOrder = async (highlight: HighlightArticle, newOrder: number) => {
        try {
            await VanityAPI.saveHighlight({ ...highlight, displayOrder: newOrder });
            init();
        } catch (err) {
            showToast('Failed to update order');
        }
    };

    const moveItem = async (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === highlights.length - 1) return;

        const newHighlights = [...highlights];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap locally for UI response
        [newHighlights[index], newHighlights[targetIndex]] = [newHighlights[targetIndex], newHighlights[index]];

        // Re-assign displayOrder locally
        const updatedHighlights = newHighlights.map((item, idx) => ({
            ...item,
            displayOrder: idx
        }));

        setHighlights(updatedHighlights);

        try {
            // Atomic Reorder Calls (Rule 14)
            await Promise.all([
                VanityAPI.reorderHighlight(newHighlights[index].id, index),
                VanityAPI.reorderHighlight(newHighlights[targetIndex].id, targetIndex)
            ]);
        } catch (err) {
            console.error('Failed to move', err);
            showToast('Failed to save order');
            init(); // Revert on failure
        }
    };

    // TAGS Logic
    const addTag = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            const newTags = [...(editingItem?.tags || [])];
            if (!newTags.includes(tagInput.trim())) {
                newTags.push(tagInput.trim());
                setEditingItem(prev => prev ? { ...prev, tags: newTags } : null);
            }
            setTagInput('');
        }
    };
    const removeTag = (tag: string) => {
        setEditingItem(prev => prev ? { ...prev, tags: (prev.tags || []).filter(t => t !== tag) } : null);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        const d = new Date(dateString);
        return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // EDITOR RENDER
    if (view === 'editor' && editingItem) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm animate-fadeIn">
                {/* Header / Actions */}
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center sticky top-0 z-20">
                    <button onClick={() => setView('list')} className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-black">
                        &larr; Back
                    </button>
                    <div className="font-serif font-bold text-lg">{editingItem.id === 'new' ? 'New Highlight' : 'Editing Highlight'}</div>
                    <button
                        onClick={handleSave}
                        disabled={!editingItem.title || !editingItem.slug}
                        className={`px-6 py-2 rounded text-[10px] font-black uppercase tracking-widest transition-all ${(!editingItem.title || !editingItem.slug) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-black text-white hover:bg-vanity-pink shadow-md'}`}
                    >
                        Save
                    </button>
                </div>
                {/* Editor Tabs */}
                <div className="px-6 pt-3 flex gap-4 border-b bg-gray-50 sticky top-[64px] z-10">
                    <button onClick={() => setEditTab('content')} className={`pb-3 px-2 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${editTab === 'content' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-black'}`}>Article Content</button>
                    <button onClick={() => setEditTab('seo')} className={`pb-3 px-2 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${editTab === 'seo' ? 'border-vanity-pink text-vanity-pink' : 'border-transparent text-gray-400 hover:text-black'}`}>SEO Registry</button>
                </div>

                <div className="p-8 space-y-8">
                    {editTab === 'content' && (
                        <div className="animate-fadeIn space-y-8">
                            {/* META SECTION */}
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                                <div className="lg:col-span-8 space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Title</label>
                                        <input
                                            name="title"
                                            type="text"
                                            value={editingItem.title || ''}
                                            onChange={e => {
                                                const newTitle = e.target.value;
                                                // Rule 18: Auto Slug Generation
                                                const autoSlug = newTitle
                                                    .toLowerCase()
                                                    .replace(/[^a-z0-9]+/g, '-')
                                                    .replace(/^-+|-+$/g, '');

                                                // Only update slug if it matches the "auto" version of the *previous* title (heuristic)
                                                // Or fairly standard: if slug is empty or they haven't manually touched it? 
                                                // For now, let's just update it if slug is empty OR acts as a direct mirror.
                                                // User asked: "when I type a title, the slug should automatically generate". 
                                                // We will enforce this unless user manually edits slug.

                                                setEditingItem(prev => ({
                                                    ...prev,
                                                    title: newTitle,
                                                    slug: (!prev?.id || prev.id === 'new') ? autoSlug : prev.slug // Auto-gen only for new or empty? User said "when I type title". Let's do it always for now, but usually safer to check isDirty.
                                                }));
                                            }}
                                            className="w-full text-2xl font-serif font-bold border-b border-gray-200 py-2 outline-none focus:border-vanity-pink"
                                            placeholder="Highlight Title..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Slug (Auto-generated)</label>
                                        <input
                                            name="slug"
                                            type="text"
                                            value={editingItem.slug || ''}
                                            onChange={e => setEditingItem({ ...editingItem, slug: e.target.value })}
                                            className="w-full font-mono text-xs bg-gray-50 p-2.5 rounded border border-transparent focus:bg-white focus:border-gray-200"
                                            placeholder="url-slug"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Assigned Magazine</label>
                                            <select
                                                value={editingItem.issueId || ''}
                                                onChange={e => setEditingItem({ ...editingItem, issueId: e.target.value })}
                                                className="w-full p-2.5 bg-gray-50 rounded font-bold text-xs border-r-[10px] border-r-transparent outline-none"
                                            >
                                                <option value="">Website Highlight (No Magazine)</option>
                                                {issues.map(i => (
                                                    <option key={i.id} value={i.id}>{i.title} ({i.periodText})</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</label>
                                            <select
                                                value={editingItem.status || 'draft'}
                                                onChange={e => setEditingItem({ ...editingItem, status: e.target.value })}
                                                className="w-full p-2.5 bg-gray-50 rounded font-bold text-xs border-r-[10px] border-r-transparent outline-none"
                                            >
                                                <option value="draft">Draft</option>
                                                <option value="published">Published</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Short Summary (For Lists)</label>
                                        <RichTextEditor
                                            name="text"
                                            value={editingItem.text || ''}
                                            onChange={val => setEditingItem({ ...editingItem, text: val })}
                                            height="100px"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Narrative Content (Main Article)</label>
                                        <RichTextEditor
                                            name="content"
                                            value={editingItem.content || ''}
                                            onChange={val => {
                                                console.log("DEBUG: Narrative content changed:", { valLen: val.length });
                                                setEditingItem({ ...editingItem, content: val });
                                            }}
                                            height="300px"
                                        />
                                    </div>

                                    {/* TAGS */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tags (Press Enter)</label>
                                        <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded min-h-[40px]">
                                            {(editingItem.tags || []).map(t => (
                                                <span key={t} className="bg-black text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide flex items-center gap-2">
                                                    {t}
                                                    <button onClick={() => removeTag(t)} className="hover:text-vanity-pink">×</button>
                                                </span>
                                            ))}
                                            <input
                                                type="text"
                                                value={tagInput}
                                                onChange={e => setTagInput(e.target.value)}
                                                onKeyDown={addTag}
                                                className="bg-transparent outline-none text-sm font-bold min-w-[100px] flex-1"
                                                placeholder="Add tag..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* COVER IMAGE */}
                                <div className="lg:col-span-4 space-y-8">
                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Cover Image</label>
                                        <div
                                            onClick={() => { setUploadTarget('cover'); if (fileInputRef.current) fileInputRef.current.click(); }}
                                            className="aspect-[3/4] bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center overflow-hidden hover:border-vanity-pink transition-all relative group cursor-pointer shadow-md"
                                        >
                                            {editingItem.imageUrl ? (
                                                <img src={editingItem.imageUrl} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="text-center p-4">
                                                    <span className="font-bold text-gray-300 block mb-1 text-sm">Upload Cover</span>
                                                    <span className="text-[8px] text-gray-400 uppercase">Portrait 3:4 Recommended</span>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                <span className="text-white font-black uppercase text-[10px] tracking-widest">Change Cover</span>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Visual Description (Alt Text)</label>
                                            <input
                                                type="text"
                                                value={editingItem.heroAltText || ''}
                                                onChange={e => setEditingItem({ ...editingItem, heroAltText: e.target.value })}
                                                className="w-full bg-white border border-gray-200 rounded p-2.5 font-sans text-[11px] focus:ring-2 focus:ring-vanity-pink outline-none"
                                                placeholder="Describe for accessibility..."
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</label>
                                        <select
                                            value={editingItem.categoryId || ''}
                                            onChange={e => setEditingItem({ ...editingItem, categoryId: e.target.value })}
                                            className="w-full p-2.5 bg-gray-50 rounded font-bold text-xs border-r-[10px] border-r-transparent outline-none"
                                        >
                                            <option value="">-- No Category --</option>
                                            {categories.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <hr className="border-gray-100" />

                            {/* CONTENT EDITOR */}
                            <div className="space-y-8">
                                <h3 className="font-serif text-2xl font-bold italic">Article Content</h3>
                                <ContentModuleEditor
                                    modules={editingItem.contentModules || []}
                                    onChange={(mods) => setEditingItem({ ...editingItem, contentModules: mods })}
                                    onImageRequest={(modId) => {
                                        setUploadTarget('module');
                                        setModuleTargetId(modId);
                                        if (fileInputRef.current) fileInputRef.current.click();
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {editTab === 'seo' && (
                        <div className="animate-fadeIn space-y-8">
                            {/* SEO Panel */}
                            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                                <SEOPanel
                                    seoTitle={editingItem.seoTitle || ''}
                                    seoDescription={editingItem.seoDescription || ''}
                                    seoKeywords={editingItem.seoKeywords || ''}
                                    ogTitle={editingItem.ogTitle || ''}
                                    ogDescription={editingItem.ogDescription || ''}
                                    canonicalUrl={editingItem.canonicalUrl || ''}
                                    seoScore={editingItem.seoScore}
                                    slug={editingItem.slug || ''}
                                    contentForAI={`${editingItem.title || ''} ${editingItem.text || ''} ${editingItem.content || ''} ${editingItem.excerpt || ''}`.trim()}
                                    baseUrl="https://vanity-magazine.com/highlights"
                                    onChange={(fields) => {
                                        setEditingItem(prev => prev ? ({
                                            ...prev,
                                            ...fields
                                        }) : null);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* HIDDEN UPLOADERS */}
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                        if (e.target.files?.[0]) {
                            setStashedFile(e.target.files[0]);
                            setShowUploader(true);
                        }
                    }}
                />

                {showUploader && stashedFile && (
                    <StandardImageUploader
                        isOpen={showUploader}
                        onClose={() => { setShowUploader(false); setStashedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        initialFile={stashedFile}
                        aspectRatio={uploadTarget === 'cover' ? 3 / 4 : 1}
                        entityType="highlight"
                        entityId={editingItem.id || 'new'}
                        role={uploadTarget}
                        modalTitle={uploadTarget === 'cover' ? "Highlight Cover" : "Content Image"}
                        onUploadComplete={(img) => {
                            if (uploadTarget === 'cover') {
                                setEditingItem(prev => prev ? {
                                    ...prev,
                                    imageUrl: img.url_full || img.url_original,
                                    heroImageId: img.id
                                } : null);
                            } else if (uploadTarget === 'module' && moduleTargetId) {
                                // Update module image
                                const newMods = (editingItem.contentModules || []).map(m =>
                                    m.id === moduleTargetId ? { ...m, imageUrl: img.url_full || img.url_original, imageId: img.id } : m
                                );
                                setEditingItem(prev => prev ? { ...prev, contentModules: newMods } : null);
                            }
                            setShowUploader(false);
                            setStashedFile(null);
                        }}
                    />
                )}
            </div>
        );
    }

    // LIST RENDER
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden animate-fadeIn">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <span className="font-black text-[12px] uppercase text-gray-400 tracking-[0.2em]">Independent Highlights</span>
                <button
                    onClick={handleCreate}
                    className="bg-black text-white px-8 py-2.5 rounded text-[11px] font-black uppercase tracking-widest hover:bg-vanity-pink transition-all shadow-md"
                >
                    + Create Highlight
                </button>
            </div>

            <div className="divide-y divide-gray-100">
                {loading ? (
                    <div className="p-10 text-center text-gray-400">Loading...</div>
                ) : highlights.length === 0 ? (
                    <div className="p-10 text-center text-gray-400">No highlights found. Create one above.</div>
                ) : (
                    highlights
                        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                        .map((h, index) => {
                            const issue = issues.find(i => i.id === h.issueId);
                            return (
                                <div
                                    key={h.id}
                                    className="p-4 flex items-center justify-between hover:bg-gray-50 transition-all group"
                                >
                                    <div className="flex items-center gap-4">
                                        {/* Order Controls */}
                                        <div className="flex flex-col gap-1 items-center">
                                            <button
                                                onClick={() => moveItem(index, 'up')}
                                                disabled={index === 0}
                                                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 disabled:opacity-20 text-gray-500 font-bold text-xs"
                                                title="Move Up"
                                            >
                                                ▲
                                            </button>
                                            <span className="text-[10px] font-mono text-gray-300 font-bold">{index + 1}</span>
                                            <button
                                                onClick={() => moveItem(index, 'down')}
                                                disabled={index === highlights.length - 1}
                                                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 disabled:opacity-20 text-gray-500 font-bold text-xs"
                                                title="Move Down"
                                            >
                                                ▼
                                            </button>
                                        </div>

                                        <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                                            {h.imageUrl ? (
                                                <img src={h.imageUrl} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[9px] font-black text-gray-300 uppercase">No IMG</div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="font-bold text-lg text-black flex items-center gap-3">
                                                {h.title || 'Untitled'}
                                                {/* Status Toggle */}
                                                <div
                                                    onClick={async () => {
                                                        const newStatus = h.status === 'published' ? 'draft' : 'published';
                                                        try {
                                                            // Optimistic update
                                                            setHighlights(prev => prev.map(p => p.id === h.id ? { ...p, status: newStatus } : p));
                                                            await VanityAPI.saveHighlight({ ...h, status: newStatus });
                                                        } catch (err) {
                                                            showToast('Failed to update status');
                                                            init();
                                                        }
                                                    }}
                                                    className={`cursor-pointer relative flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${h.status === 'published' ? 'bg-green-500' : 'bg-gray-200'}`}
                                                >
                                                    <span
                                                        className={`block h-4 w-4 rounded-full bg-white transition-transform ${h.status === 'published' ? 'translate-x-6' : 'translate-x-1'}`}
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-gray-400 text-xs font-black uppercase tracking-widest mt-1 flex items-center gap-2">
                                                <span>Slug: {h.slug}</span>
                                                <span>&middot;</span>
                                                {issue ? `Issue: ${issue.title} ` : <span className="text-vanity-pink">WEBSITE HIGHLIGHT</span>}
                                                <span>&middot;</span>
                                                <span>Created: {formatDate(h.createdAt)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEdit(h.id)}
                                            className="bg-black text-white px-4 py-1.5 rounded text-[9px] font-black uppercase tracking-widest hover:bg-vanity-pink transition-all"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(h.id)}
                                            className="text-red-500 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest hover:text-red-700 transition-all"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                )}
            </div>

            {/* TOAST SYSTEM */}
            {toast && (
                <div className="fixed bottom-12 left-1/2 -translate-x-1/2 z-[100] bg-black text-white px-10 py-5 rounded-full shadow-2xl border-l-[8px] border-vanity-pink font-black text-xs uppercase tracking-[0.3em] animate-fadeIn whitespace-nowrap">
                    {toast}
                </div>
            )}
        </div>
    );

};

export default AdminHighlights;
